/* ──────────────────────────────────────────────
 *  Shared date helpers for the booking tunnel.
 *  Fonctions pures.
 * ────────────────────────────────────────────── */

// La validation `YYYY-MM-DD` vit dans `@cfixe/shared` avec le reste du contrat des query params du
// tunnel (`?d=`) : elle sert aussi à l'app pro, qui construit ces liens.
import { isValidISODate } from '@cfixe-monorepo/shared';

/** YYYY-MM-DD from a Date, using local timezone */
export function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse a YYYY-MM-DD string into a local Date (midnight) */
export function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Add n CALENDAR days to a Date, keeping its local time of day. Never `n × 24 h`: across the
 * CEST → CET change, Monday 00:00 + 7 × 24 h lands on Sunday 23:00, so « next week » looped back
 * onto the same week and no slot after the last Sunday of October was reachable.
 */
export function addDays(d: Date, n: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}

/** Get the Monday of the week containing d */
export function getMonday(d: Date): Date {
  const copy = new Date(d);
  const day = copy.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diff);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

/** Get the Monday ISO string of the week containing d */
export function getMondayISO(d: Date): string {
  return toISODate(getMonday(d));
}

export { isValidISODate };

/** Nombre de jours calendaires de `from` à `to` (YYYY-MM-DD), négatif si `to` précède `from`. */
export function daysBetween(from: string, to: string): number {
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000);
}

/**
 * Début de la fenêtre de 7 jours de la grille de créneaux qui contient `date`. Les fenêtres partent
 * d'AUJOURD'HUI (aujourd'hui, + 7, + 14…) et non du lundi : la bande de jours ne commence jamais par
 * des jours passés grisés (bug 34). Le backend cherche 7 jours à partir de la date envoyée : la fenêtre
 * affichée et la fenêtre cherchée coïncident. Une date passée retombe sur aujourd'hui.
 */
export function slotWindowStart(date: string, today: string): string {
  const offset = daysBetween(today, date);
  if (offset <= 0) return today;
  return toISODate(addDays(parseLocalDate(today), Math.floor(offset / 7) * 7));
}

/** Dernier jour (inclus) de la fenêtre de 7 jours qui commence à `windowStart`. */
export function slotWindowEnd(windowStart: string): string {
  return toISODate(addDays(parseLocalDate(windowStart), 6));
}

/**
 * Fenêtre sur laquelle ouvrir la grille de créneaux pour une date demandée par lien profond
 * (`?d=YYYY-MM-DD`).
 *
 * Renvoie `undefined` — donc « laisse le tunnel décider » — quand la date est absente, malformée,
 * ou déjà passée : un lien partagé survit à la date qu'il visait, et ouvrir la grille dans le passé
 * n'afficherait aucun créneau.
 */
export function resolveInitialWindowStart(isoDate: string | undefined, now: Date = new Date()): string | undefined {
  if (!isoDate || !isValidISODate(isoDate)) return undefined;
  const today = toISODate(now);
  return isoDate >= today ? slotWindowStart(isoDate, today) : undefined;
}
